/**
 * IAM namespaces: partition the flat {@link Permission} enum into product topics.
 *
 * - **Single assignment:** each permission appears in exactly one namespace (enforced by
 *   {@link assertIamNamespaceCoverage}).
 * - **Order:** grouped roughly platform → member engagement → clinical → domain facets →
 *   tenant/ops → transfers/quality/files/docket. Order is documentation-only; not relied on for behavior.
 */
import { Permission } from '../permissions/permissions.enum';
import type { IamNamespaceDefinition } from '../roles/types';

export const IAM_NAMESPACES: readonly IamNamespaceDefinition[] = [
  // --- Platform: internal users, admin surfaces, engineering ---

  {
    id: 'userAdmin',
    title: 'User administration',
    description:
      'Internal users, enrollment, impersonation, app access, and training orchestration.',
    permissions: [
      Permission.CAN_CREATE_USERS,
      Permission.CAN_IMPERSONATE_USERS,
      Permission.CAN_ADMINISTER_APP_ACCESS,
      Permission.CAN_ADMINISTER_IAM,
      Permission.CAN_ENROLL_MEMBERS,
      Permission.CAN_ORCHESTRATE_TRAINING,
    ],
  },
  {
    id: 'adminPortal',
    title: 'Admin portal and tools',
    description: 'Access to admin surfaces and broad API bypass used by trusted operators.',
    permissions: [
      Permission.CAN_ACCESS_ADMIN_TOOLS,
      Permission.CAN_ACCESS_ADMIN_PORTAL,
      Permission.CAN_MAKE_ALL_API_CALLS,
    ],
  },
  {
    id: 'engineering',
    title: 'Engineering and operations',
    description: 'Scripts, tests, seed data, and internal Slack administration.',
    permissions: [
      Permission.CAN_RUN_SCRIPTS,
      Permission.CAN_RUN_TESTS,
      Permission.CAN_GENERATE_SEED_DATA,
      Permission.CAN_ADMIN_SLACK,
    ],
  },

  // --- Member: directory, list filters, messaging, telephony ---

  {
    id: 'memberData',
    title: 'Member directory and profile data',
    description:
      'Visibility and updates to member records, including cross-member access and view-all affordances.',
    permissions: [
      Permission.CAN_VIEW_MEMBERS,
      Permission.CAN_SEE_ALL_USER_DATA,
      Permission.CAN_UPDATE_ALL_USER_DATA,
      Permission.CAN_ACCESS_ALL_MEMBERS,
      Permission.CAN_USE_VIEW_ALL_FEATURE,
    ],
  },
  {
    id: 'memberFilters',
    title: 'Member list filters',
    description: 'Filter members by client, guide, segment, and status in care tooling.',
    permissions: [
      Permission.CAN_FILTER_CLIENT,
      Permission.CAN_FILTER_GUIDE,
      Permission.CAN_FILTER_SEGMENT,
      Permission.CAN_FILTER_STATUS,
    ],
  },
  {
    id: 'messaging',
    title: 'Messaging',
    description: 'Patient messaging assignment, send, SMS visibility, and templates.',
    permissions: [
      Permission.CAN_ASSIGN_PATIENT_MESSAGE,
      Permission.CAN_SEND_PATIENT_MESSAGE,
      Permission.CAN_SEE_SMS_MESSAGES,
      Permission.CAN_USE_MESSAGE_TEMPLATE_FEATURE,
    ],
  },
  {
    id: 'telephony',
    title: 'Telephony',
    description:
      'Calls, voicemails, recordings, reassignment, subjects, and transcript tooling.',
    permissions: [
      Permission.CAN_CALL_MEMBERS,
      Permission.CAN_SEE_ALL_CALLS,
      Permission.CAN_CHANGE_PHONE_CALL_SUBJECT,
      Permission.CAN_SEE_ALL_VOICEMAILS,
      Permission.CAN_LISTEN_TO_CALL_RECORDINGS,
      Permission.CAN_REASSIGN_CALLS_AND_TEXTS,
      Permission.CAN_RERUN_CALL_TRANSCRIPTIONS,
    ],
  },

  // --- Clinical: notes, visits, hospitalizations, appointments ---

  {
    id: 'clinicalNotesAndVisits',
    title: 'Clinical notes and visits',
    description: 'Notes, visits, and scheduling events on the clinical timeline.',
    permissions: [
      Permission.CAN_SEE_NOTES,
      Permission.CAN_CREATE_NOTES,
      Permission.CAN_SEE_VISITS,
      Permission.CAN_CREATE_VISITS,
      Permission.CAN_SCHEDULE_EVENTS,
    ],
  },
  {
    id: 'hospitalizations',
    title: 'Hospitalizations',
    description: 'Hospitalization visibility and updates, including all-members views.',
    permissions: [
      Permission.CAN_SEE_HOSPITALIZATIONS,
      Permission.CAN_UPDATE_HOSPITALIZATIONS,
      Permission.CAN_SEE_ALL_HOSPITALIZATIONS,
    ],
  },
  {
    id: 'appointments',
    title: 'Appointments',
    description: 'Cross-member appointment visibility on the guide docket.',
    permissions: [Permission.CAN_SEE_ALL_APPOINTMENTS],
  },

  // --- Domain facets (split: needs vs feedback vs health summary vs surveys vs care path) ---

  {
    id: 'needs',
    title: 'Member needs',
    description: 'Needs visibility, updates, and destructive actions on need items.',
    permissions: [
      Permission.CAN_SEE_USER_NEEDS,
      Permission.CAN_UPDATE_USER_NEEDS,
      Permission.CAN_DELETE_NEED_ACTIONS,
    ],
  },
  {
    id: 'memberFeedback',
    title: 'Member feedback',
    description: 'Comments and operational member feedback management.',
    permissions: [Permission.CAN_COMMENT, Permission.CAN_MANAGE_MEMBER_FEEDBACK],
  },
  {
    id: 'healthSummary',
    title: 'Health summary',
    description: 'Health summary responses and summary-specific feedback.',
    permissions: [
      Permission.CAN_MANAGE_HEALTH_SUMMARY_RESPONSES,
      Permission.CAN_PROVIDE_MEMBER_SUMMARY_FEEDBACK,
    ],
  },
  {
    id: 'surveys',
    title: 'Surveys',
    description: 'Survey administration, completion, viewing, and reopening.',
    permissions: [
      Permission.CAN_ADMINISTRATE_SURVEYS,
      Permission.CAN_COMPLETE_SURVEYS,
      Permission.CAN_VIEW_SURVEYS,
      Permission.CAN_REOPEN_SURVEYS,
    ],
  },
  {
    id: 'carePath',
    title: 'Care path (stages and assignment)',
    description:
      'Member stages and workflow configuration and assignment—not survey administration.',
    permissions: [
      Permission.CAN_SEE_STAGES,
      Permission.CAN_CONFIGURE_WORKFLOWS,
      Permission.CAN_MANAGE_ASSIGNED_WORKFLOWS,
    ],
  },

  // --- Tenant & operational configuration ---

  {
    id: 'clients',
    title: 'Clients',
    description: 'Client directory and configuration, including destructive client changes.',
    permissions: [
      Permission.CAN_ADMINISTER_CLIENTS,
      Permission.CAN_MODIFY_CLIENTS,
      Permission.CAN_VIEW_CLIENTS,
    ],
  },
  {
    id: 'alerts',
    title: 'Alerts',
    description: 'Operational alerts: view and administer.',
    permissions: [Permission.CAN_ADMINISTER_ALERTS, Permission.CAN_VIEW_ALERTS],
  },
  {
    id: 'changeLogs',
    title: 'Change logs',
    description: 'Change log visibility and administration.',
    permissions: [Permission.CAN_ADMINISTER_CHANGE_LOGS, Permission.CAN_VIEW_CHANGE_LOGS],
  },

  // --- Member workflow: status & transfers, call quality, files, guide targets ---

  {
    id: 'memberStatus',
    title: 'Member status change requests',
    description: 'Create, audit, escalate, and alter member status change workflows.',
    permissions: [
      Permission.CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS,
      Permission.CAN_CREATE_MEMBER_STATUS_CHANGE_REQUESTS,
      Permission.CAN_REQUEST_ADMIN_LEVEL_STATUSES,
      Permission.CAN_ALTER_COMPLETE_MEMBER_STATUS_REQUESTS,
    ],
  },
  {
    id: 'memberTransfers',
    title: 'Member transfer requests',
    description: 'Transfer request creation and audit, with and without guide context.',
    permissions: [
      Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS,
      Permission.CAN_CREATE_MEMBER_TRANSFER_REQUESTS,
      Permission.CAN_CREATE_MEMBER_TRANSFER_REQUESTS_WITHOUT_GUIDE,
      Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS_WITHOUT_GUIDE,
    ],
  },
  {
    id: 'callQuality',
    title: 'Call quality',
    description: 'Call review assignment and engagement policy overrides.',
    permissions: [
      Permission.CAN_ASSIGN_CALL_REVIEWS,
      Permission.CAN_OVERRIDE_UNDER_TWO_MINUTE_ENGAGEMENT,
    ],
  },
  {
    id: 'files',
    title: 'Files',
    description: 'Cross-member and operational file visibility.',
    permissions: [Permission.CAN_SEE_ALL_FILES],
  },
  {
    id: 'docket',
    title: 'Docket',
    description: 'Docket embed experiences in care tooling.',
    permissions: [Permission.CAN_USE_DOCKET_EMBED_FEATURE],
  },
  {
    id: 'guideTargets',
    title: 'Guide targets',
    description: 'Guide target visibility and updates.',
    permissions: [Permission.CAN_UPDATE_GUIDE_TARGETS, Permission.CAN_VIEW_GUIDE_TARGETS],
  },
] as const;

// ---------------------------------------------------------------------------
// Coverage guard (keep namespaces in sync with Permission enum)
// ---------------------------------------------------------------------------

/** All values of the string enum as a set, for comparison against namespace assignments. */
const enumValues = (): Set<Permission> =>
  new Set(Object.values(Permission) as Permission[]);

/**
 * Validates that {@link IAM_NAMESPACES} partitions {@link Permission} exactly once.
 *
 * Throws when:
 * - the same permission is listed in more than one namespace;
 * - any enum member is missing from all namespaces;
 * - any namespace lists a string not present on the enum (should not happen with TypeScript).
 *
 * Call from tests (recommended) or one-off diagnostics when adding enum members.
 */
export function assertIamNamespaceCoverage(): void {
  const seen = new Map<Permission, string>();
  for (const ns of IAM_NAMESPACES) {
    for (const p of ns.permissions) {
      if (seen.has(p)) {
        throw new Error(
          `IAM namespace duplicate: permission ${p} in "${ns.id}" and "${seen.get(p)}"`,
        );
      }
      seen.set(p, ns.id);
    }
  }

  const expected = enumValues();
  if (seen.size !== expected.size) {
    const missing = [...expected].filter(p => !seen.has(p));
    const extra = [...seen.keys()].filter(p => !expected.has(p));
    throw new Error(
      `IAM namespace coverage mismatch (expected ${expected.size}, got ${seen.size}). Missing: ${missing.join(', ') || '(none)'}. Extra or unknown: ${extra.join(', ') || '(none)'}.`,
    );
  }
}
